import type { OverlaySnapshot } from "../app/PlaybackController";
import type { Settings } from "../app/Settings";

/**
 * Two small DOM pieces (section 22): the compact info panel, and a
 * separate, non-intrusive status banner used only for the "waiting for
 * data" and "transport error" states (section 30). Both only touch the
 * DOM when their rendered content actually changes, not every frame.
 */
export class Overlay {
  private panel: HTMLDivElement;
  private banner: HTMLDivElement;
  private lastPanelKey = "";
  private lastBannerKey = "";

  constructor(root: HTMLElement, private settings: Settings) {
    this.panel = document.createElement("div");
    this.panel.className = "coin-overlay";
    root.appendChild(this.panel);

    this.banner = document.createElement("div");
    this.banner.className = "coin-status-banner";
    root.appendChild(this.banner);
  }

  update(snapshot: OverlaySnapshot): void {
    this.updatePanel(snapshot);
    this.updateBanner(snapshot);
  }

  private updatePanel(snapshot: OverlaySnapshot): void {
    const visible = this.settings.overlayVisible;
    this.panel.dataset.hidden = visible ? "false" : "true";
    if (!visible) return;

    const kind = snapshot.tour?.isClosed ? "CLOSED TOUR" : "OPEN TOUR";
    const title = kind;
    const idPrefix = snapshot.tour ? snapshot.tour.id.slice(0, 12) : "—";
    const moveLine = `Move ${Math.min(snapshot.moveNumber, snapshot.moveTotal)} / ${snapshot.moveTotal}`;
    const foundLine = snapshot.tour ? `Found ${snapshot.tour.timesFound} times` : "";
    const genLine = snapshot.tour
      ? `Gen ${snapshot.tour.firstGeneration} → ${snapshot.tour.lastGeneration}`
      : "";
    const representsLine = snapshot.tour
      ? `Represents ${snapshot.tour.representedCount} rotation${snapshot.tour.representedCount === 1 ? "" : "s"}`
      : "";

    const legendLine = "Gold = current path · dashed coral = closed edge";
    const key = [title, idPrefix, moveLine, foundLine, genLine, representsLine, legendLine].join("|");
    if (key === this.lastPanelKey) return;
    this.lastPanelKey = key;

    this.panel.replaceChildren();
    const titleEl = document.createElement("div");
    titleEl.className = "coin-overlay-title";
    titleEl.textContent = title;

    const hashEl = document.createElement("div");
    hashEl.className = "coin-overlay-row coin-overlay-hash";
    hashEl.textContent = `${idPrefix}…`;

    this.panel.append(titleEl, hashEl, ...this.rows([moveLine, foundLine, genLine, representsLine]));
    const legend = document.createElement("div");
    legend.className = "coin-overlay-note";
    legend.textContent = legendLine;
    this.panel.appendChild(legend);
  }

  private updateBanner(snapshot: OverlaySnapshot): void {
    const visible = snapshot.errorKind !== null;
    const kind = snapshot.errorKind ?? "";
    const text =
      snapshot.errorKind === "empty"
        ? "Waiting for the first stored Knight's Tour solution…"
        : snapshot.errorKind === "transport"
          ? `⚠ ${snapshot.errorMessage ?? "connection problem"} — retrying…`
          : "";

    const key = `${visible}|${kind}|${text}`;
    if (key === this.lastBannerKey) return;
    this.lastBannerKey = key;

    this.banner.dataset.visible = String(visible);
    this.banner.dataset.kind = kind;
    this.banner.textContent = text;
  }

  private rows(lines: string[]): HTMLDivElement[] {
    return lines
      .filter((line) => line.length > 0)
      .map((line) => {
        const row = document.createElement("div");
        row.className = "coin-overlay-row";
        row.textContent = line;
        return row;
      });
  }

  dispose(): void {
    this.panel.remove();
    this.banner.remove();
  }
}
